import React from 'react';

import images from 'assets/images';
import PageLayout from 'components/common/PageLayout';
import { ENTERPRISE_CONTACT_LINK } from 'components/constants';

import styles from './API.module.scss';

const valuePillars = [
  {
    number: '01',
    title: 'Full interaction coverage',
    text: 'Capture page views, navigation, clicks, answers, form submissions, completions and performance signals.',
  },
  {
    number: '02',
    title: 'Real-time event delivery',
    text: 'Receive interaction events while the journey is active, so the next system can respond while intent is still fresh.',
  },
  {
    number: '03',
    title: 'Flexible payloads',
    text: 'Send the fields and context your workflow needs in JSON, XML or a custom payload defined by your team.',
  },
  {
    number: '04',
    title: 'Direct system connectivity',
    text: 'Connect through your backend or integration layer to CRM, analytics, databases, automation and internal products.',
  },
];

const outcomes = [
  {
    title: 'Enrich customer profiles',
    text: 'Add declared preferences, answers and live intent to the CRM records and customer profiles your teams already use.',
  },
  {
    title: 'Personalize the next step',
    text: 'Use known customer context and current-session signals to shape what the user sees or where the journey continues.',
  },
  {
    title: 'Trigger business workflows',
    text: 'Route a qualified lead, validate eligibility, save progress, start a follow-up or hand the user into an existing process.',
  },
  {
    title: 'Measure in your existing stack',
    text: 'Send engagement, completion and performance events to the analytics and reporting environment used across the organization.',
  },
];

const enterprisePrinciples = [
  {
    title: 'Events originate in the experience',
    text: 'Each selected interaction is mapped to an event with the relevant response, journey and session context.',
  },
  {
    title: 'Data flows to your endpoint',
    text: 'The client sends the event to the backend or integration layer your technical team controls.',
  },
  {
    title: 'Your logic decides what happens next',
    text: 'Store, validate, enrich, analyze or activate the data according to your own systems and policies.',
  },
];

const API: React.FC = () => (
  <PageLayout isFullWidth isWhiteHeader isMarketingHeader showFooterCta={false}>
    <main className={styles.page}>
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>Real-time experience API</p>
            <h1>Bring every Storycards interaction into your systems.</h1>
            <p className={styles.heroText}>
              Capture clicks, answers, navigation, submissions, completions and performance signals as they happen.
              Send the events and context your teams need directly to your backend, CRM, analytics platform, data
              warehouse or internal product.
            </p>
            <div className={styles.heroActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>

          <div className={styles.heroPromise}>
            <span>A rich, configurable API surface</span>
            <div>
              <strong>Listen to the signals you need</strong>
              <p>Choose the interaction events and journey context that matter to each workflow.</p>
            </div>
            <div>
              <strong>Shape every payload</strong>
              <p>Deliver data in the format and structure your receiving system expects.</p>
            </div>
          </div>
        </div>

        <figure className={styles.heroVisual}>
          <img src={images.securityHero} alt="Storycards API connecting live interaction data to enterprise systems" />
        </figure>
      </section>

      <section className={styles.statement}>
        <div className={styles.sectionInner}>
          <h2>From the client to your backend, in real time.</h2>
          <div>
            <p>
              Storycards maps the interactions you select as events and sends them from the experience to your
              backend. Your systems can use the data immediately to update a profile, personalize the next step,
              trigger a workflow or record the event in your source of truth.
            </p>
            <p>
              Your developers define the connection, payload and business logic. Business teams continue building and
              improving the experience in Studio without waiting for development on every content change.
            </p>
          </div>
        </div>
      </section>

      <section className={styles.valueSection}>
        <div className={styles.sectionInner}>
          <header className={styles.valueHeader}>
            <p className={styles.eyebrow}>API capabilities</p>
            <h2>More than form submissions.</h2>
          </header>
          <div className={styles.valueGrid}>
            {valuePillars.map((item) => (
              <article key={item.title}>
                <span>{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.outcomesSection}>
        <div className={styles.sectionInner}>
          <header className={styles.sectionHeader}>
            <div>
              <p className={styles.eyebrow}>From interaction to action</p>
              <h2>Turn live interaction data into business action.</h2>
            </div>
            <p>
              Use Storycards events during the experience or after completion, inside the systems that already run
              your customer journey.
            </p>
          </header>
          <div className={styles.outcomeGrid}>
            {outcomes.map((item, index) => (
              <article key={item.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.operatingSection}>
        <div className={styles.sectionInner}>
          <header className={styles.operatingHeader}>
            <p className={styles.eyebrow}>Client-to-backend by design</p>
            <h2>Your developers define the connection once. Business teams keep creating.</h2>
            <p>
              Data moves into the environment your technical team controls. From there, your own logic determines
              what is stored, analyzed, enriched or activated.
            </p>
          </header>
          <div className={styles.operatingGrid}>
            {enterprisePrinciples.map((item, index) => (
              <article key={item.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2>Put real-time Storycards data to work in your stack.</h2>
          <div>
            <p>Talk with our enterprise team about the events, payloads and systems behind your customer journey.</p>
            <div className={styles.ctaActions}>
              <a href={ENTERPRISE_CONTACT_LINK} className={styles.primaryButton}>
                Talk to our enterprise team <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  </PageLayout>
);

export default API;
